import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronDown,
  CircleHelp,
  Download,
  FileCheck2,
  FileSignature,
  FileText,
  Gauge,
  Home,
  Info,
  Leaf,
  LoaderCircle,
  SunMedium,
  UploadCloud,
  Trash2,
  X,
  Zap,
} from "lucide-react";

import "./RenovationProjectWorksBudgetStep.css";
import { useToast } from "../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   ENERGY CARRIERS
   ========================================================= */

const ENERGY_CARRIER_OPTIONS = [
  {
    value: "ELECTRICITY",
    labelKey: "energyCarriers.electricity",
    fallback: "Electricity",
  },
  {
    value: "NATURAL_GAS",
    labelKey: "energyCarriers.naturalGas",
    fallback: "Natural gas",
  },
  {
    value: "GAS_DIESEL_COAL",
    labelKey: "energyCarriers.gasDieselCoal",
    fallback: "Gas / Diesel / Coal",
  },
  {
    value: "LPG",
    labelKey: "energyCarriers.lpg",
    fallback: "LPG",
  },
  {
    value: "DISTRICT_HEATING",
    labelKey: "energyCarriers.districtHeating",
    fallback: "District heating",
  },
  {
    value: "OTHER",
    labelKey: "energyCarriers.other",
    fallback: "Other",
  },
];

/* =========================================================
   ENERGY DATA SOURCES
   ========================================================= */

const ENERGY_DATA_SOURCE_OPTIONS = [
  {
    value: "FINAL_EPC",
    labelKey: "energySources.finalEpc",
    fallback: "Final EPC",
  },
  {
    value: "PROJECT_EPC",
    labelKey: "energySources.projectEpc",
    fallback: "Project / mid-project EPC",
  },
  {
    value: "PRE_ASSESSMENT",
    labelKey: "energySources.preAssessment",
    fallback: "Pre-assessment",
  },
  {
    value: "RENOVATION_PLAN",
    labelKey: "energySources.renovationPlan",
    fallback: "Renovation plan",
  },
  {
    value: "OSS_ESTIMATION",
    labelKey: "energySources.ossEstimation",
    fallback: "OSS estimation",
  },
];

/* =========================================================
   INITIAL FORM
   ========================================================= */

const EMPTY_FORM = {
  works_budget_is_signed_contract: false,

  energy_data_source: "",

  previous_consumption_kwh_year: "",
  post_renovation_consumption_kwh_year: "",

  replaced_final_energy: "",
  replaced_energy_comments: "",

  final_energy_saved: "",
  final_energy_saved_comments: "",

  final_renewable_energy_production_kwh_year: "",
  installed_capacity_kwp: "",
  renovated_area_sqm: "",
};

/* =========================================================
   HELPERS
   ========================================================= */

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "WORKS_BUDGET") || {
      step_code: "WORKS_BUDGET",
      step_order: 5,
      status: "NOT_STARTED",
      is_optional: false,
      visible_to: "HB_OSS",
      editable_by: "HB_OSS",
    }
  );
}

function statusClass(status) {
  return String(status || "NOT_STARTED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function documentFilename(document) {
  return document?.filename || document?.original_filename || "";
}

function parseNonNegativeNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

function formatCalculatedNumber(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "";
  }

  return value.toFixed(2);
}

function validatePdf(file) {
  if (!(file instanceof File)) {
    return false;
  }

  const filename = String(file.name || "").toLowerCase();

  if (!filename.endsWith(".pdf")) {
    return false;
  }

  const type = String(file.type || "").toLowerCase();

  if (
    type &&
    ![
      "application/pdf",
      "application/x-pdf",
      "application/octet-stream",
    ].includes(type)
  ) {
    return false;
  }

  return true;
}

/* =========================================================
   FIELD TOOLTIP
   ========================================================= */

function FieldTooltip({ text, label }) {
  return (
    <span className="home-renovation-tooltip-wrap">
      <button
        type="button"
        className="home-renovation-tooltip-trigger"
        aria-label={label}
      >
        <CircleHelp size={15} />
      </button>

      <span className="home-renovation-tooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

/* =========================================================
   FIELD LABEL
   ========================================================= */

function FieldLabel({
  children,
  tooltip,
  tooltipLabel,
  requirement = "optional",
  t,
}) {
  return (
    <span className="home-renovation-field-label">
      <span className="home-renovation-field-label__main">
        {children}

        <FieldTooltip text={tooltip} label={tooltipLabel} />
      </span>

      {requirement === "required" ? (
        <span className="home-renovation-requirement home-renovation-requirement--required">
          {t("requirements.requiredForCompletion", "Required for completion")}
        </span>
      ) : (
        <span className="home-renovation-requirement">
          {t("requirements.optional", "Optional")}
        </span>
      )}
    </span>
  );
}

/* =========================================================
   DOCUMENT SLOT
   ========================================================= */

function DocumentSlot({
  icon: Icon,
  title,
  description,
  tooltip,
  requirement = "required",

  file,
  existingDocument,

  canUpload,
  unavailableText,

  onBrowse,
  onDrop,
  onRemovePending,
  onDeleteExisting,
  deletingExisting = false,

  inputRef,
  onInputChange,

  accept = ".pdf,application/pdf",

  t,
}) {
  const existingFilename = documentFilename(existingDocument);

  const downloadUrl =
    existingDocument?.download_url || existingDocument?.file_url || "";

  const hasExisting = Boolean(existingDocument);

  const hasPending = file instanceof File;

  return (
    <section
      className={`home-renovation-document-slot ${
        hasExisting ? "has-existing" : ""
      } ${hasPending ? "has-pending" : ""}`}
    >
      <div className="home-renovation-document-slot__header">
        <div className="home-renovation-document-slot__title">
          <span className="home-renovation-document-slot__icon">
            <Icon size={19} />
          </span>

          <div>
            <div className="home-renovation-document-slot__title-line">
              <strong>{title}</strong>

              <FieldTooltip
                text={tooltip}
                label={t("tooltips.moreInformation", "More information")}
              />
            </div>

            <p>{description}</p>
          </div>
        </div>

        <span
          className={
            requirement === "required"
              ? "home-renovation-requirement home-renovation-requirement--required"
              : "home-renovation-requirement"
          }
        >
          {requirement === "required"
            ? t("requirements.requiredForCompletion", "Required for completion")
            : t("requirements.optional", "Optional")}
        </span>
      </div>

      {hasExisting ? (
        <div className="home-renovation-existing-document">
          <div className="home-renovation-existing-document__icon">
            <FileCheck2 size={19} />
          </div>

          <div className="home-renovation-existing-document__content">
            <span>{t("documents.currentFile", "Current file")}</span>

            <strong>
              {existingFilename || t("documents.document", "Document")}
            </strong>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "6px",
            }}
          >
            {downloadUrl ? (
              <a
                href={downloadUrl}
                className="home-renovation-document-download"
                target="_blank"
                rel="noreferrer"
              >
                <Download size={15} />

                {t("buttons.download", "Download")}
              </a>
            ) : null}

            {onDeleteExisting ? (
              <button
                type="button"
                className="home-renovation-pending-document__remove"
                onClick={onDeleteExisting}
                disabled={deletingExisting}
                aria-label={t("buttons.deleteDocument", "Delete document")}
                title={t("buttons.deleteDocument", "Delete document")}
              >
                {deletingExisting ? (
                  <LoaderCircle size={15} />
                ) : (
                  <Trash2 size={15} />
                )}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasPending ? (
        <div className="home-renovation-pending-document">
          <div className="home-renovation-pending-document__icon">
            <UploadCloud size={18} />
          </div>

          <div className="home-renovation-pending-document__content">
            <span>
              {hasExisting
                ? t("documents.replacementSelected", "Replacement selected")
                : t("documents.fileSelected", "File selected")}
            </span>

            <strong>{file.name}</strong>
          </div>

          <button
            type="button"
            className="home-renovation-pending-document__remove"
            onClick={onRemovePending}
            aria-label={t("buttons.removeSelectedFile", "Remove selected file")}
          >
            <X size={15} />
          </button>
        </div>
      ) : null}

      {canUpload ? (
        <>
          <button
            type="button"
            className="home-renovation-dropzone"
            onClick={onBrowse}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDrop={onDrop}
          >
            <UploadCloud size={20} />

            <span>
              <strong>
                {hasExisting
                  ? t("documents.replaceFile", "Replace document")
                  : t("documents.addFile", "Add document")}
              </strong>

              <small>
                {t(
                  "documents.dragDrop",
                  "Drag & drop a PDF here or click to browse.",
                )}
              </small>
            </span>
          </button>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={onInputChange}
            hidden
          />
        </>
      ) : (
        <div className="home-renovation-document-readonly">
          <Info size={17} />

          <span>{unavailableText}</span>
        </div>
      )}
    </section>
  );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function RenovationProjectWorksBudgetStep() {
  const { t } = useTranslation("renovation-project-works-budget");

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const {
    project,
    steps,
    basePath,
    viewerRole,
    registerStepSaveHandler,
    setStepDirty,
    refreshProject,
  } = useOutletContext();

  const step = findStep(steps);

  /* =======================================================
     SERVER STATE
     ======================================================= */

  const [form, setForm] = React.useState(EMPTY_FORM);

  const [worksBudgetDocument, setWorksBudgetDocument] = React.useState(null);

  const [worksContractDocument, setWorksContractDocument] =
    React.useState(null);

  const [finalEpcDocument, setFinalEpcDocument] = React.useState(null);

  const [stepStatus, setStepStatus] = React.useState(
    step.status || "NOT_STARTED",
  );

  const [loading, setLoading] = React.useState(true);

  const [loadError, setLoadError] = React.useState("");

  const [saving, setSaving] = React.useState(false);

  const [deletingDocumentKind, setDeletingDocumentKind] = React.useState("");

  /* =======================================================
     PENDING FILES
     ======================================================= */

  const [worksBudgetFile, setWorksBudgetFile] = React.useState(null);

  const [worksContractFile, setWorksContractFile] = React.useState(null);

  const [finalEpcFile, setFinalEpcFile] = React.useState(null);

  const budgetInputRef = React.useRef(null);

  const contractInputRef = React.useRef(null);

  const finalEpcInputRef = React.useRef(null);

  /* =======================================================
     PERMISSIONS
     ======================================================= */

  const canUploadDocuments = viewerRole === "HB";

  const canEditEnergy = ["HB", "OSS"].includes(viewerRole);

  /* =======================================================
     API HELPERS
     ======================================================= */

  const requestJson = React.useCallback(
    async (url, options = {}) => {
      const token = await getAccessTokenSilently();

      const isFormData = options.body instanceof FormData;

      const response = await fetch(`${API_BASE}${url}`, {
        ...options,

        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          ...(!isFormData && options.body
            ? { "Content-Type": "application/json" }
            : {}),
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(
          data?.detail ||
            t("toasts.requestFailed", "The request could not be completed."),
        );

        error.status = response.status;
        error.payload = data;

        throw error;
      }

      return data;
    },
    [getAccessTokenSilently, t],
  );

  const applyHomeRenovationResponse = React.useCallback(
    (data) => {
      const record =
        data?.home_renovation ||
        data?.works_budget ||
        data?.record ||
        data ||
        {};

      const nextForm = {
        ...EMPTY_FORM,

        works_budget_is_signed_contract: Boolean(
          record?.works_budget_is_signed_contract ??
          data?.works_budget_is_signed_contract ??
          false,
        ),

        energy_data_source:
          record?.energy_data_source ?? data?.energy_data_source ?? "",

        previous_consumption_kwh_year:
          record?.previous_consumption_kwh_year ??
          data?.previous_consumption_kwh_year ??
          "",

        post_renovation_consumption_kwh_year:
          record?.post_renovation_consumption_kwh_year ??
          data?.post_renovation_consumption_kwh_year ??
          "",

        replaced_final_energy:
          record?.replaced_final_energy ?? data?.replaced_final_energy ?? "",

        replaced_energy_comments:
          record?.replaced_energy_comments ??
          data?.replaced_energy_comments ??
          "",

        final_energy_saved:
          record?.final_energy_saved ?? data?.final_energy_saved ?? "",

        final_energy_saved_comments:
          record?.final_energy_saved_comments ??
          data?.final_energy_saved_comments ??
          "",

        final_renewable_energy_production_kwh_year:
          record?.final_renewable_energy_production_kwh_year ??
          data?.final_renewable_energy_production_kwh_year ??
          "",

        installed_capacity_kwp:
          record?.installed_capacity_kwp ?? data?.installed_capacity_kwp ?? "",

        renovated_area_sqm:
          record?.renovated_area_sqm ?? data?.renovated_area_sqm ?? "",
      };

      setForm(nextForm);

      setWorksBudgetDocument(
        data?.works_budget_document ?? record?.works_budget_document ?? null,
      );

      setWorksContractDocument(
        data?.works_contract_document ??
          record?.works_contract_document ??
          null,
      );

      setFinalEpcDocument(
        data?.final_epc_document ?? record?.final_epc_document ?? null,
      );

      setStepStatus(
        data?.step_status ||
          record?.step_status ||
          step.status ||
          "NOT_STARTED",
      );
    },
    [step.status],
  );

  /* =======================================================
     LOAD HOME RENOVATION
     GET /api/projects/<id>/home-renovation/
     ======================================================= */

  const loadHomeRenovation = React.useCallback(async () => {
    if (!project?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");

    try {
      const data = await requestJson(
        `/api/projects/${project.id}/home-renovation/`,
      );

      applyHomeRenovationResponse(data);

      setStepDirty?.(false);
    } catch (error) {
      console.error("Failed to load Home Renovation:", error);

      setLoadError(
        error?.message ||
          t(
            "toasts.loadFailedMessage",
            "The Home Renovation information could not be loaded.",
          ),
      );
    } finally {
      setLoading(false);
    }
  }, [applyHomeRenovationResponse, project?.id, requestJson, setStepDirty, t]);

  React.useEffect(() => {
    loadHomeRenovation();
  }, [loadHomeRenovation]);

  /* =======================================================
     FORM CHANGE
     ======================================================= */

  const updateField = React.useCallback(
    (fieldName, value) => {
      setForm((current) => ({
        ...current,
        [fieldName]: value,
      }));

      setStepDirty?.(true);
    },
    [setStepDirty],
  );

  /* =======================================================
     CALCULATED SAVINGS
     ======================================================= */

  const previousConsumption = parseNonNegativeNumber(
    form.previous_consumption_kwh_year,
  );

  const postConsumption = parseNonNegativeNumber(
    form.post_renovation_consumption_kwh_year,
  );

  const consumptionComparisonInvalid =
    previousConsumption !== null &&
    postConsumption !== null &&
    postConsumption > previousConsumption;

  const annualSavings =
    previousConsumption !== null &&
    postConsumption !== null &&
    !consumptionComparisonInvalid
      ? previousConsumption - postConsumption
      : null;

  /* =======================================================
     DOCUMENT FILE SELECTION
     ======================================================= */

  const selectPdf = React.useCallback(
    (file, type) => {
      if (!file) {
        return;
      }

      if (!validatePdf(file)) {
        showToast({
          type: "error",
          title: t("toasts.invalidPdfTitle", "PDF required"),
          message: t(
            "toasts.invalidPdfMessage",
            "Please select a valid PDF document.",
          ),
        });

        return;
      }

      if (type === "budget") {
        setWorksBudgetFile(file);
      } else if (type === "contract") {
        setWorksContractFile(file);
      } else if (type === "finalEpc") {
        setFinalEpcFile(file);
      }

      setStepDirty?.(true);
    },
    [setStepDirty, showToast, t],
  );

  const handleFileInput = (event, type) => {
    const file = event.target.files?.[0];

    if (file) {
      selectPdf(file, type);
    }

    event.target.value = "";
  };

  const handleDrop = (event, type) => {
    event.preventDefault();

    const file = event.dataTransfer.files?.[0];

    if (file) {
      selectPdf(file, type);
    }
  };

  const clearPendingFile = React.useCallback(
    (type) => {
      if (type === "budget") {
        setWorksBudgetFile(null);
      } else if (type === "contract") {
        setWorksContractFile(null);
      } else if (type === "finalEpc") {
        setFinalEpcFile(null);
      }

      setStepDirty?.(true);
    },
    [setStepDirty],
  );

  /* =======================================================
     SIGNED BUDGET AS CONTRACT
     ======================================================= */

  const toggleSignedBudgetContract = () => {
    const nextValue = !Boolean(form.works_budget_is_signed_contract);

    updateField("works_budget_is_signed_contract", nextValue);

    if (nextValue) {
      setWorksContractFile(null);
    }
  };

  /* =======================================================
     SAVE FORM
     PATCH /api/projects/<id>/home-renovation/
     ======================================================= */

  const saveForm = React.useCallback(async () => {
    const payload = {
      works_budget_is_signed_contract: Boolean(
        form.works_budget_is_signed_contract,
      ),

      energy_data_source: form.energy_data_source || "",

      previous_consumption_kwh_year: form.previous_consumption_kwh_year ?? "",

      post_renovation_consumption_kwh_year:
        form.post_renovation_consumption_kwh_year ?? "",

      replaced_final_energy: form.replaced_final_energy || "",

      replaced_energy_comments: form.replaced_energy_comments || "",

      final_energy_saved: form.final_energy_saved || "",

      final_energy_saved_comments: form.final_energy_saved_comments || "",

      final_renewable_energy_production_kwh_year:
        form.final_renewable_energy_production_kwh_year ?? "",

      installed_capacity_kwp: form.installed_capacity_kwp ?? "",

      renovated_area_sqm: form.renovated_area_sqm ?? "",
    };

    const data = await requestJson(
      `/api/projects/${project.id}/home-renovation/`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    applyHomeRenovationResponse(data);

    return data;
  }, [applyHomeRenovationResponse, form, project?.id, requestJson]);

  /* =======================================================
     DOCUMENT UPLOAD
     ======================================================= */

  const uploadDocument = React.useCallback(
    async (kind, file) => {
      if (!(file instanceof File)) {
        return null;
      }

      const formData = new FormData();
      formData.append("file", file);

      const url =
        kind === "final-epc"
          ? `/api/projects/${project.id}/home-renovation/final-epc/document/`
          : `/api/projects/${project.id}/home-renovation/documents/${kind}/`;

      const data = await requestJson(url, {
        method: "POST",
        body: formData,
      });

      applyHomeRenovationResponse(data);

      return data;
    },
    [applyHomeRenovationResponse, project?.id, requestJson],
  );

  /* =======================================================
     DOCUMENT DELETE
     ======================================================= */

  const deleteExistingDocument = React.useCallback(
    async (kind) => {
      if (!project?.id || deletingDocumentKind) {
        return;
      }

      const url =
        kind === "final-epc"
          ? `/api/projects/${project.id}/home-renovation/final-epc/document/`
          : `/api/projects/${project.id}/home-renovation/documents/${kind}/`;

      setDeletingDocumentKind(kind);

      try {
        /* Persist any current HB/OSS field edits before changing a
           document so the DELETE response cannot overwrite unsaved
           energy information with the older server version. */
        await saveForm();

        const data = await requestJson(url, {
          method: "DELETE",
        });

        applyHomeRenovationResponse(data);

        if (kind === "works-budget") {
          setWorksBudgetFile(null);
        } else if (kind === "works-contract") {
          setWorksContractFile(null);
        } else if (kind === "final-epc") {
          setFinalEpcFile(null);
        }

        await refreshProject?.();

        const hasOtherPendingFile = Boolean(
          (kind !== "works-budget" && worksBudgetFile instanceof File) ||
          (kind !== "works-contract" && worksContractFile instanceof File) ||
          (kind !== "final-epc" && finalEpcFile instanceof File),
        );

        setStepDirty?.(hasOtherPendingFile);

        showToast({
          type: "success",
          title: t("toasts.documentDeletedTitle", "Document removed"),
          message: t(
            "toasts.documentDeletedMessage",
            "The document was removed successfully.",
          ),
        });
      } catch (error) {
        console.error("Failed to delete Home Renovation document:", error);

        showToast({
          type: "error",
          title: t(
            "toasts.documentDeleteFailedTitle",
            "Could not remove document",
          ),
          message:
            error?.message ||
            t(
              "toasts.documentDeleteFailedMessage",
              "The document could not be removed.",
            ),
        });
      } finally {
        setDeletingDocumentKind("");
      }
    },
    [
      applyHomeRenovationResponse,
      deletingDocumentKind,
      finalEpcFile,
      project?.id,
      refreshProject,
      requestJson,
      saveForm,
      setStepDirty,
      showToast,
      t,
      worksBudgetFile,
      worksContractFile,
    ],
  );

  /* =======================================================
     SAVE COMPLETE CURRENT STEP STATE

     Missing "required for completion" values do not block
     navigation. The backend decides whether the workflow
     status is NOT_STARTED / IN_PROGRESS / COMPLETED.
     ======================================================= */

  const saveStep = React.useCallback(
    async ({ showSuccessToast = false } = {}) => {
      if (!project?.id || saving) {
        return false;
      }

      setSaving(true);

      try {
        await saveForm();

        if (worksBudgetFile instanceof File) {
          await uploadDocument("works-budget", worksBudgetFile);
          setWorksBudgetFile(null);
        }

        if (
          !form.works_budget_is_signed_contract &&
          worksContractFile instanceof File
        ) {
          await uploadDocument("works-contract", worksContractFile);
          setWorksContractFile(null);
        }

        if (finalEpcFile instanceof File) {
          await uploadDocument("final-epc", finalEpcFile);
          setFinalEpcFile(null);
        }

        await refreshProject?.();

        setStepDirty?.(false);

        if (showSuccessToast) {
          showToast({
            type: "success",
            title: t("toasts.savedTitle", "Home Renovation saved"),
            message: t(
              "toasts.savedMessage",
              "The Home Renovation information was saved successfully.",
            ),
          });
        }

        return true;
      } catch (error) {
        console.error("Failed to save Home Renovation:", error);

        const errors = error?.payload?.errors;
        const firstError =
          errors && typeof errors === "object"
            ? Object.values(errors).flat().filter(Boolean).map(String)[0]
            : "";

        showToast({
          type: "error",
          title: t("toasts.saveFailedTitle", "Could not save Home Renovation"),
          message:
            firstError ||
            error?.message ||
            t(
              "toasts.saveFailedMessage",
              "Please check the information and try again.",
            ),
        });

        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      finalEpcFile,
      form.works_budget_is_signed_contract,
      project?.id,
      refreshProject,
      saveForm,
      saving,
      setStepDirty,
      showToast,
      t,
      uploadDocument,
      worksBudgetFile,
      worksContractFile,
    ],
  );

  /* =======================================================
     SHARED LAYOUT SAVE HANDLER
     ======================================================= */

  React.useEffect(() => {
    if (!registerStepSaveHandler) {
      return undefined;
    }

    return registerStepSaveHandler(() =>
      saveStep({
        showSuccessToast: false,
      }),
    );
  }, [registerStepSaveHandler, saveStep]);

  /* =======================================================
     DISPLAY STATUS
     ======================================================= */

  const hasUnsavedDraft = Boolean(
    worksBudgetFile instanceof File ||
    worksContractFile instanceof File ||
    finalEpcFile instanceof File,
  );

  const displayStatus =
    stepStatus === "COMPLETED"
      ? "COMPLETED"
      : hasUnsavedDraft
        ? "IN_PROGRESS"
        : stepStatus || step.status || "NOT_STARTED";

  /* =======================================================
     CONTINUE
     ======================================================= */

  const goNext = async () => {
    const saved = await saveStep({
      showSuccessToast: false,
    });

    if (!saved) {
      return;
    }

    navigate(`${basePath}/${project.id}/subsidies-grants`);
  };

  /* =======================================================
     RENDER
     ======================================================= */

  if (loading) {
    return (
      <article className="home-renovation-step">
        <div className="home-renovation-completion-note">
          <LoaderCircle size={18} />

          <div>
            <strong>{t("loading.title", "Loading Home Renovation")}</strong>

            <span>
              {t(
                "loading.description",
                "Loading renovation documents and energy information...",
              )}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (loadError) {
    return (
      <article className="home-renovation-step">
        <div className="home-renovation-completion-note">
          <Info size={18} />

          <div>
            <strong>
              {t("errors.loadTitle", "Home Renovation could not be loaded")}
            </strong>

            <span>{loadError}</span>

            <button
              type="button"
              className="home-renovation-document-download"
              onClick={loadHomeRenovation}
              style={{ marginTop: "8px", alignSelf: "flex-start" }}
            >
              {t("buttons.retry", "Try again")}
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="home-renovation-step">
      {/* ===================================================
          HEADER
          =================================================== */}

      <header className="home-renovation-step__header">
        <div className="home-renovation-step__heading">
          <span className="home-renovation-step__eyebrow">
            <Home size={15} />

            {t("header.step", "Step 5")}
          </span>

          <div className="home-renovation-step__title-row">
            <h1>{t("header.title", "Home Renovation")}</h1>

            <span className="home-renovation-step__scope">
              <BadgeCheck size={14} />

              {t("header.shared", "Homebuyer + OSS")}
            </span>
          </div>

          <p>
            {t(
              "header.subtitle",
              "Manage the renovation documents and record the expected energy impact of the completed works.",
            )}
          </p>
        </div>

        <div className="home-renovation-step__status">
          <span className="home-renovation-step__stage-count">
            {t("header.stage", "Stage {{current}} of {{total}}", {
              current: step.step_order || 5,
              total: 8,
            })}
          </span>

          <span
            className={
              `home-renovation-step__status-pill ` +
              `home-renovation-step__status-pill--${statusClass(displayStatus)}`
            }
          >
            {t(
              `stepStatuses.${displayStatus}`,
              String(displayStatus).replaceAll("_", " ").toLowerCase(),
            )}
          </span>
        </div>
      </header>

      {/* ===================================================
          NAVIGATION / COMPLETION NOTE
          =================================================== */}

      <div className="home-renovation-completion-note">
        <Info size={18} />

        <div>
          <strong>
            {t(
              "completionNote.title",
              "You can continue even if some information is not available yet.",
            )}
          </strong>

          <span>
            {t(
              "completionNote.description",
              "Items marked “Required for completion” will be needed before this stage can ultimately be completed, but they do not prevent you from moving between project stages.",
            )}
          </span>
        </div>
      </div>

      {/* ===================================================
          DOCUMENTS
          =================================================== */}

      <section className="home-renovation-section home-renovation-section--documents">
        <div className="home-renovation-section__header">
          <div className="home-renovation-section__heading">
            <span className="home-renovation-section__icon">
              <FileSignature size={20} />
            </span>

            <div>
              <h2>{t("documents.title", "Renovation documents")}</h2>

              <p>
                {t(
                  "documents.subtitle",
                  "Documents supporting the agreed renovation works and their final technical outcome.",
                )}
              </p>
            </div>
          </div>

          {!canUploadDocuments ? (
            <span className="home-renovation-section__readonly">
              {t("documents.homebuyerManaged", "Uploads managed by Homebuyer")}
            </span>
          ) : null}
        </div>

        <div className="home-renovation-documents-grid">
          {/* WORKS BUDGET */}

          <DocumentSlot
            icon={FileText}
            title={t("documents.worksBudget.title", "Works budget")}
            description={t(
              "documents.worksBudget.description",
              "Final approved quotation or cost breakdown agreed with the contractor.",
            )}
            tooltip={t(
              "documents.worksBudget.tooltip",
              "Upload the final approved quotation or cost breakdown for the renovation works, as agreed between the homebuyer and the contractor.",
            )}
            requirement="required"
            file={worksBudgetFile}
            existingDocument={worksBudgetDocument}
            canUpload={canUploadDocuments}
            unavailableText={t(
              "documents.homebuyerUploadOnly",
              "The Homebuyer is responsible for uploading this document.",
            )}
            onBrowse={() => budgetInputRef.current?.click()}
            onDrop={(event) => handleDrop(event, "budget")}
            onRemovePending={() => clearPendingFile("budget")}
            onDeleteExisting={
              canUploadDocuments && worksBudgetDocument
                ? () => deleteExistingDocument("works-budget")
                : undefined
            }
            deletingExisting={deletingDocumentKind === "works-budget"}
            inputRef={budgetInputRef}
            onInputChange={(event) => handleFileInput(event, "budget")}
            t={t}
          />

          {/* WORKS CONTRACT */}

          <DocumentSlot
            icon={FileCheck2}
            title={t("documents.contract.title", "Works contract")}
            description={
              form.works_budget_is_signed_contract
                ? t(
                    "documents.contract.coveredDescription",
                    "The signed works budget is being used as the works contract.",
                  )
                : t(
                    "documents.contract.description",
                    "Signed agreement between the homebuyer and contractor covering the renovation works.",
                  )
            }
            tooltip={t(
              "documents.contract.tooltip",
              "Upload the signed contract covering the renovation works. A signed budget may also be treated as the contract, so a separate document is not required when that option is selected.",
            )}
            requirement={
              form.works_budget_is_signed_contract ? "optional" : "required"
            }
            file={worksContractFile}
            existingDocument={worksContractDocument}
            canUpload={
              canUploadDocuments && !form.works_budget_is_signed_contract
            }
            unavailableText={
              form.works_budget_is_signed_contract
                ? t(
                    "documents.contract.signedBudgetUsed",
                    "A separate contract is not needed because the signed works budget will be used as the contract.",
                  )
                : t(
                    "documents.homebuyerUploadOnly",
                    "The Homebuyer is responsible for uploading this document.",
                  )
            }
            onBrowse={() => contractInputRef.current?.click()}
            onDrop={(event) => handleDrop(event, "contract")}
            onRemovePending={() => clearPendingFile("contract")}
            onDeleteExisting={
              canUploadDocuments && worksContractDocument
                ? () => deleteExistingDocument("works-contract")
                : undefined
            }
            deletingExisting={deletingDocumentKind === "works-contract"}
            inputRef={contractInputRef}
            onInputChange={(event) => handleFileInput(event, "contract")}
            t={t}
          />

          {/* SIGNED BUDGET = CONTRACT */}

          <div className="home-renovation-signed-budget-option">
            <button
              type="button"
              className={`home-renovation-checkbox ${
                form.works_budget_is_signed_contract ? "is-checked" : ""
              }`}
              onClick={toggleSignedBudgetContract}
              disabled={!canEditEnergy}
              aria-pressed={Boolean(form.works_budget_is_signed_contract)}
            >
              <span className="home-renovation-checkbox__box">
                {form.works_budget_is_signed_contract ? (
                  <Check size={13} />
                ) : null}
              </span>

              <span className="home-renovation-checkbox__content">
                <strong>
                  {t(
                    "documents.signedBudget.title",
                    "Use the signed works budget as the contract",
                  )}
                </strong>

                <small>
                  {t(
                    "documents.signedBudget.description",
                    "Select this when the approved budget has been signed and serves as the contractual agreement.",
                  )}
                </small>
              </span>
            </button>

            <FieldTooltip
              text={t(
                "documents.signedBudget.tooltip",
                "The reviewer confirmed that a signed budget can be considered the works contract. When selected, a separate works-contract upload is not required.",
              )}
              label={t(
                "tooltips.signedBudget",
                "Information about using the signed budget as the contract",
              )}
            />
          </div>

          {/* FINAL EPC */}

          <DocumentSlot
            icon={Leaf}
            title={t("documents.finalEpc.title", "Final EPC")}
            description={t(
              "documents.finalEpc.description",
              "Final energy performance certificate, when one is produced for the renovation.",
            )}
            tooltip={t(
              "documents.finalEpc.tooltip",
              "The Final EPC is optional because it is not generated for every renovation. Where it is unavailable, the energy values may instead come from the technical project, pre-assessment, project EPC or an OSS estimation.",
            )}
            requirement="optional"
            file={finalEpcFile}
            existingDocument={finalEpcDocument}
            canUpload={canUploadDocuments}
            unavailableText={t(
              "documents.finalEpc.homebuyerUpload",
              "The Homebuyer normally uploads the Final EPC when one is available.",
            )}
            onBrowse={() => finalEpcInputRef.current?.click()}
            onDrop={(event) => handleDrop(event, "finalEpc")}
            onRemovePending={() => clearPendingFile("finalEpc")}
            onDeleteExisting={
              canUploadDocuments && finalEpcDocument
                ? () => deleteExistingDocument("final-epc")
                : undefined
            }
            deletingExisting={deletingDocumentKind === "final-epc"}
            inputRef={finalEpcInputRef}
            onInputChange={(event) => handleFileInput(event, "finalEpc")}
            t={t}
          />
        </div>
      </section>

      {/* ===================================================
          ENERGY IMPROVEMENT
          =================================================== */}

      <section className="home-renovation-section home-renovation-section--energy">
        <div className="home-renovation-section__header">
          <div className="home-renovation-section__heading">
            <span className="home-renovation-section__icon">
              <Zap size={20} />
            </span>

            <div>
              <h2>{t("energy.title", "Energy improvement")}</h2>

              <p>
                {t(
                  "energy.subtitle",
                  "Record the available final or estimated energy-performance values for the renovation.",
                )}
              </p>
            </div>
          </div>

          <span className="home-renovation-section__shared-edit">
            <BadgeCheck size={14} />

            {t("energy.editableBy", "Homebuyer + OSS")}
          </span>
        </div>

        {/* ENERGY SOURCE */}

        <div className="home-renovation-source-row">
          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="optional"
              tooltip={t(
                "fields.energyDataSource.tooltip",
                "Indicates where the energy-performance values were obtained, for example the Final EPC, project EPC, pre-assessment, renovation plan or an OSS estimation.",
              )}
              tooltipLabel={t(
                "tooltips.energyDataSource",
                "Information about energy data source",
              )}
            >
              {t("fields.energyDataSource.label", "Energy data source")}
            </FieldLabel>

            <div className="home-renovation-select">
              <select
                value={form.energy_data_source}
                disabled={!canEditEnergy}
                onChange={(event) =>
                  updateField("energy_data_source", event.target.value)
                }
              >
                <option value="">{t("common.select", "Select")}</option>

                {ENERGY_DATA_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.fallback)}
                  </option>
                ))}
              </select>

              <ChevronDown size={17} aria-hidden="true" />
            </div>
          </label>
        </div>

        <div className="home-renovation-energy-grid">
          {/* PREVIOUS CONSUMPTION */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.previousConsumption.tooltip",
                "Annual energy consumption of the property before renovation. This may come from the initial EPC, pre-assessment, renovation plan, project EPC or an OSS assessment.",
              )}
              tooltipLabel={t(
                "tooltips.previousConsumption",
                "Information about previous consumption",
              )}
            >
              {t("fields.previousConsumption.label", "Previous consumption")}
            </FieldLabel>

            <div className="home-renovation-unit-input">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form.previous_consumption_kwh_year}
                disabled={!canEditEnergy}
                placeholder="0.00"
                onChange={(event) =>
                  updateField(
                    "previous_consumption_kwh_year",
                    event.target.value,
                  )
                }
              />

              <span>kWh/year</span>
            </div>
          </label>

          {/* POST RENOVATION */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.postConsumption.tooltip",
                "Estimated annual energy consumption after the renovation. It may be extracted from the Final EPC or entered from an OSS technical estimation.",
              )}
              tooltipLabel={t(
                "tooltips.postConsumption",
                "Information about post-renovation consumption",
              )}
            >
              {t("fields.postConsumption.label", "Post-renovation consumption")}
            </FieldLabel>

            <div className="home-renovation-unit-input">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                className={consumptionComparisonInvalid ? "is-warning" : ""}
                value={form.post_renovation_consumption_kwh_year}
                disabled={!canEditEnergy}
                placeholder="0.00"
                onChange={(event) =>
                  updateField(
                    "post_renovation_consumption_kwh_year",
                    event.target.value,
                  )
                }
              />

              <span>kWh/year</span>
            </div>

            {consumptionComparisonInvalid ? (
              <small className="home-renovation-field-warning">
                {t(
                  "validation.postGreaterThanPrevious",
                  "Post-renovation consumption is currently higher than previous consumption. You can keep navigating, but this value will need to be corrected before final completion.",
                )}
              </small>
            ) : null}
          </label>

          {/* ANNUAL ENERGY SAVINGS */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.annualSavings.tooltip",
                "Annual energy savings are calculated automatically as previous consumption minus post-renovation consumption.",
              )}
              tooltipLabel={t(
                "tooltips.annualSavings",
                "Information about annual energy savings",
              )}
            >
              {t("fields.annualSavings.label", "Annual energy savings")}
            </FieldLabel>

            <div className="home-renovation-unit-input home-renovation-unit-input--calculated">
              <input
                type="text"
                readOnly
                value={formatCalculatedNumber(annualSavings)}
                placeholder="—"
              />

              <span>kWh/year</span>
            </div>

            <small className="home-renovation-calculated-note">
              <Gauge size={13} />

              {t("fields.annualSavings.calculated", "Calculated automatically")}
            </small>
          </label>

          {/* RENOVATED AREA */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.renovatedArea.tooltip",
                "Total floor area affected by the renovation measures.",
              )}
              tooltipLabel={t(
                "tooltips.renovatedArea",
                "Information about renovated area",
              )}
            >
              {t("fields.renovatedArea.label", "Renovated area")}
            </FieldLabel>

            <div className="home-renovation-unit-input">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form.renovated_area_sqm}
                disabled={!canEditEnergy}
                placeholder="0.00"
                onChange={(event) =>
                  updateField("renovated_area_sqm", event.target.value)
                }
              />

              <span>m²</span>
            </div>
          </label>

          {/* REPLACED FINAL ENERGY */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.replacedFinalEnergy.tooltip",
                "Type of energy carrier that is being replaced or reduced by the renovation, such as natural gas being replaced by an electric heat pump.",
              )}
              tooltipLabel={t(
                "tooltips.replacedFinalEnergy",
                "Information about replaced final energy",
              )}
            >
              {t("fields.replacedFinalEnergy.label", "Replaced final energy")}
            </FieldLabel>

            <div className="home-renovation-select">
              <select
                value={form.replaced_final_energy}
                disabled={!canEditEnergy}
                onChange={(event) =>
                  updateField("replaced_final_energy", event.target.value)
                }
              >
                <option value="">{t("common.select", "Select")}</option>

                {ENERGY_CARRIER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.fallback)}
                  </option>
                ))}
              </select>

              <ChevronDown size={17} aria-hidden="true" />
            </div>
          </label>

          {/* FINAL ENERGY SAVED */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.finalEnergySaved.tooltip",
                "Type of final energy whose consumption will be reduced as a result of the renovation.",
              )}
              tooltipLabel={t(
                "tooltips.finalEnergySaved",
                "Information about final energy saved",
              )}
            >
              {t("fields.finalEnergySaved.label", "Final energy saved")}
            </FieldLabel>

            <div className="home-renovation-select">
              <select
                value={form.final_energy_saved}
                disabled={!canEditEnergy}
                onChange={(event) =>
                  updateField("final_energy_saved", event.target.value)
                }
              >
                <option value="">{t("common.select", "Select")}</option>

                {ENERGY_CARRIER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.fallback)}
                  </option>
                ))}
              </select>

              <ChevronDown size={17} aria-hidden="true" />
            </div>
          </label>

          {/* REPLACED ENERGY COMMENTS */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="optional"
              tooltip={t(
                "fields.replacedEnergyComments.tooltip",
                "Additional notes describing how conventional energy sources are being replaced, for example replacing a gas boiler with a heat pump.",
              )}
              tooltipLabel={t(
                "tooltips.replacedEnergyComments",
                "Information about comments on replaced energy",
              )}
            >
              {t(
                "fields.replacedEnergyComments.label",
                "Comments on replaced energy",
              )}
            </FieldLabel>

            <textarea
              rows={4}
              value={form.replaced_energy_comments}
              disabled={!canEditEnergy}
              placeholder={t(
                "fields.replacedEnergyComments.placeholder",
                "Describe how the existing energy source is being replaced...",
              )}
              onChange={(event) =>
                updateField("replaced_energy_comments", event.target.value)
              }
            />
          </label>

          {/* FINAL ENERGY SAVED COMMENTS */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="optional"
              tooltip={t(
                "fields.finalEnergySavedComments.tooltip",
                "Additional explanation of the mechanism through which final-energy consumption is expected to be reduced.",
              )}
              tooltipLabel={t(
                "tooltips.finalEnergySavedComments",
                "Information about comments on final energy saved",
              )}
            >
              {t(
                "fields.finalEnergySavedComments.label",
                "Comments on final energy saved",
              )}
            </FieldLabel>

            <textarea
              rows={4}
              value={form.final_energy_saved_comments}
              disabled={!canEditEnergy}
              placeholder={t(
                "fields.finalEnergySavedComments.placeholder",
                "Add details about the expected energy savings...",
              )}
              onChange={(event) =>
                updateField("final_energy_saved_comments", event.target.value)
              }
            />
          </label>

          {/* RENEWABLE PRODUCTION */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.renewableProduction.tooltip",
                "Expected annual renewable-energy production from installed systems such as photovoltaic panels or solar thermal systems.",
              )}
              tooltipLabel={t(
                "tooltips.renewableProduction",
                "Information about renewable energy production",
              )}
            >
              {t(
                "fields.renewableProduction.label",
                "Final renewable energy production",
              )}
            </FieldLabel>

            <div className="home-renovation-unit-input">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form.final_renewable_energy_production_kwh_year}
                disabled={!canEditEnergy}
                placeholder="0.00"
                onChange={(event) =>
                  updateField(
                    "final_renewable_energy_production_kwh_year",
                    event.target.value,
                  )
                }
              />

              <span>kWh/year</span>
            </div>
          </label>

          {/* INSTALLED CAPACITY */}

          <label className="home-renovation-field">
            <FieldLabel
              t={t}
              requirement="required"
              tooltip={t(
                "fields.installedCapacity.tooltip",
                "Total installed capacity of renewable-energy systems included in the renovation.",
              )}
              tooltipLabel={t(
                "tooltips.installedCapacity",
                "Information about installed capacity",
              )}
            >
              {t("fields.installedCapacity.label", "Installed capacity")}
            </FieldLabel>

            <div className="home-renovation-unit-input">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form.installed_capacity_kwp}
                disabled={!canEditEnergy}
                placeholder="0.00"
                onChange={(event) =>
                  updateField("installed_capacity_kwp", event.target.value)
                }
              />

              <span>kWp</span>
            </div>
          </label>
        </div>

        {/* =================================================
            ENERGY SOURCE EXPLANATION
            ================================================= */}

        <div className="home-renovation-energy-info">
          <SunMedium size={18} />

          <div>
            <strong>
              {t(
                "energy.sourceInfoTitle",
                "Values can come from different technical sources.",
              )}
            </strong>

            <span>
              {t(
                "energy.sourceInfoDescription",
                "Where a Final EPC is not available, the figures may come from the renovation plan, project EPC, pre-assessment or an updated OSS estimation.",
              )}
            </span>
          </div>
        </div>
      </section>

      {/* ===================================================
          CONTINUE
          =================================================== */}

      <button
        type="button"
        className="home-renovation-next"
        onClick={goNext}
        disabled={saving}
      >
        <span>
          {saving
            ? t("buttons.saving", "Saving...")
            : t("buttons.continue", "Save and continue to Subsidies & grants")}
        </span>

        {saving ? <LoaderCircle size={18} /> : <ArrowRight size={18} />}
      </button>
    </article>
  );
}
